import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import {
  buildChildrenOf, buildHidden, toggle,
  FiltrosBarra, CardsResumen, TableHeader, FilaCargando, FilaVacia, FilaArbol, BotonPDF,
  BotonExcel, useExcelDownload,
} from "./BalanceComprobacion";
import { usePDFIndividual } from "./EstadoFinanciero";
import { useCurrency } from "../../../../shared/contexts/CurrencyContext";

// Año anterior: 31 dic del año anterior al de fechaHasta
function prevYearEnd(fechaHasta) {
  if (!fechaHasta) return null;
  const year = new Date(fechaHasta + "T00:00:00").getFullYear() - 1;
  return `${year}-12-31`;
}

const TIPO_COLORS = {
  ACTIVO:     "#60a5fa",
  PASIVO:     "#f87171",
  PATRIMONIO: "#c084fc",
};

function colorFila(fila) {
  const c = TIPO_COLORS[fila.tipo] ?? "var(--text-primary)";
  if (fila.nivel === 1) return c;
  if (fila.nivel === 2) return "var(--text-primary)";
  return fila.nivel === 3 ? "var(--text-secondary)" : "var(--text-muted)";
}

function bgFila(fila) {
  if (fila.nivel >= 3) return "transparent";
  const c = TIPO_COLORS[fila.tipo] ?? "var(--border-card)";
  return c + (fila.nivel === 1 ? "12" : "07");
}

function Separador({ label, color, numCols }) {
  return (
    <tr>
      <td colSpan={numCols} style={{ padding: "7px 14px",
        background: color + "22",
        borderLeft: `3px solid ${color}`,
        color, fontSize: "0.78rem", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.8px" }}>
        {label}
      </td>
    </tr>
  );
}

function FilaTotal({ label, monto, montoAnt, color, big, comparativo, fmtCRCSigned }) {
  const montoNum = Number(monto ?? 0);
  const montoColor = montoNum < -0.005 ? "#f87171" : color;
  return (
    <tr style={{ borderTop: `1px solid ${color}40` }}>
      <td colSpan={2} style={{ padding: big ? "10px 12px" : "7px 12px",
        paddingLeft: 26, fontWeight: 700, color, fontSize: big ? "0.88rem" : "0.83rem" }}>
        {label}
      </td>
      <td className="text-end" style={{ padding: big ? "10px 14px" : "7px 14px",
        fontFamily: "monospace", fontWeight: 800, color: montoColor,
        fontSize: big ? "0.92rem" : "0.85rem" }}>
        {fmtCRCSigned(monto)}
      </td>
      {comparativo && (
        <td className="text-end" style={{ padding: big ? "10px 14px" : "7px 14px",
          fontFamily: "monospace", fontWeight: 800,
          color: Number(montoAnt ?? 0) < -0.005 ? "#f87171" : "var(--text-secondary)",
          fontSize: big ? "0.92rem" : "0.85rem",
          borderLeft: "1px solid var(--border-card)" }}>
          {montoAnt != null ? fmtCRCSigned(montoAnt) : "—"}
        </td>
      )}
    </tr>
  );
}

export default function BalanceGeneral() {
  const { t } = useTranslation();
  const { fmtCRCSigned, isUSD, setMultimoneda } = useCurrency();

  function valorFila(fila) {
    const s = Number(isUSD ? (fila.saldo_usd ?? 0) : (fila.saldo ?? 0));
    if (Math.abs(s) < 0.01) return { text: "—", color: "var(--text-muted)" };
    const typeColor = TIPO_COLORS[fila.tipo] ?? "var(--text-primary)";
    const color = s < -0.005 ? "#f87171" : (fila.nivel <= 2 ? typeColor : "var(--text-secondary)");
    return { text: fmtCRCSigned(s), color };
  }

  function valorFilaAnt(fila, dataAnt) {
    if (!dataAnt) return { text: "—", color: "var(--text-muted)" };
    const ant = (dataAnt.filas ?? []).find(f => f.id === fila.id);
    if (!ant) return { text: "—", color: "var(--text-muted)" };
    const s = Number(isUSD ? (ant.saldo_usd ?? 0) : (ant.saldo ?? 0));
    if (Math.abs(s) < 0.01) return { text: "—", color: "var(--text-muted)" };
    const color = s < -0.005 ? "#f87171" : "var(--text-secondary)";
    return { text: fmtCRCSigned(s), color };
  }

  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

  const [fechaHasta,  setFechaHasta]  = useState(today);
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [soloConMov,  setSoloConMov]  = useState(true);
  const [collapsed,   setCollapsed]   = useState(new Set());
  const [comparativo, setComparativo] = useState(false);
  const [dataAnt,     setDataAnt]     = useState(null);
  const [loadingAnt,  setLoadingAnt]  = useState(false);

  const pdfBG = usePDFIndividual(
    "/api/finance/reportes/balance-general-pdf/",
    "situacion_financiera.pdf",
    { showComparativo: true, showNotas: true }
  );
  const excelBG = useExcelDownload(
    "/api/finance/reportes/balance-general-excel/",
    "balance_general.xlsx"
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (fechaHasta) qs.set("fecha_hasta", fechaHasta);
      const res = await apiFetch(`/api/finance/reportes/balance-general/?${qs}`);
      const d   = await res.json();
      setData(d);
      setMultimoneda(d.multimoneda_activa ?? false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fechaHasta, setMultimoneda]);

  const fetchDataAnt = useCallback(async () => {
    if (!comparativo) { setDataAnt(null); return; }
    const hasta = prevYearEnd(fechaHasta);
    if (!hasta) return;
    setLoadingAnt(true);
    try {
      const qs = new URLSearchParams();
      qs.set("fecha_hasta", hasta);
      const res = await apiFetch(`/api/finance/reportes/balance-general/?${qs}`);
      const d   = await res.json();
      setDataAnt(d);
    } catch (e) { console.error(e); }
    finally { setLoadingAnt(false); }
  }, [comparativo, fechaHasta]);

  useEffect(() => { fetchData(); },    [fetchData]);
  useEffect(() => { fetchDataAnt(); }, [fetchDataAnt]);

  const filas      = data?.filas ?? [];
  const childrenOf = buildChildrenOf(filas);
  const hidden     = buildHidden(filas, childrenOf, collapsed);

  const tieneMovArbol = (id) => {
    const f = filas.find(x => x.id === id);
    if (!f) return false;
    if (f.tiene_movimientos) return true;
    return (childrenOf[id] ?? []).some(tieneMovArbol);
  };

  const byTipo = (tipo) => filas.filter(f =>
    f.tipo === tipo && !hidden.has(f.id) && (!soloConMov || tieneMovArbol(f.id))
  );

  const activos    = byTipo("ACTIVO");
  const pasivos    = byTipo("PASIVO");
  const patrimonio = byTipo("PATRIMONIO");
  const total      = activos.length + pasivos.length + patrimonio.length;

  const tA   = Number(isUSD ? (data?.total_activo_usd            ?? 0) : (data?.total_activo            ?? 0));
  const tP   = Number(isUSD ? (data?.total_pasivo_usd            ?? 0) : (data?.total_pasivo            ?? 0));
  const tPat = Number(isUSD ? (data?.total_patrimonio_usd        ?? 0) : (data?.total_patrimonio        ?? 0));
  const tPP  = Number(isUSD ? (data?.total_pasivo_patrimonio_usd ?? 0) : (data?.total_pasivo_patrimonio ?? 0));
  const bal  = data?.balanceado ?? true;

  const tAAnt   = comparativo && dataAnt ? Number(isUSD ? (dataAnt.total_activo_usd            ?? 0) : (dataAnt.total_activo            ?? 0)) : null;
  const tPAnt   = comparativo && dataAnt ? Number(isUSD ? (dataAnt.total_pasivo_usd            ?? 0) : (dataAnt.total_pasivo            ?? 0)) : null;
  const tPatAnt = comparativo && dataAnt ? Number(isUSD ? (dataAnt.total_patrimonio_usd        ?? 0) : (dataAnt.total_patrimonio        ?? 0)) : null;
  const tPPAnt  = comparativo && dataAnt ? Number(isUSD ? (dataAnt.total_pasivo_patrimonio_usd ?? 0) : (dataAnt.total_pasivo_patrimonio ?? 0)) : null;

  const numCols   = comparativo ? 4 : 3;
  const antFecha  = prevYearEnd(fechaHasta);

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("reportes.balanceGeneral")}</span>
      </span>
      <div>
        <h2 className="title mt-2 mb-2">{t("reportes.balanceGeneral")}</h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("reportes.activosPasivosDesc")}</p>
      </div>

      {comparativo && antFecha && (
        <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
          <span style={{
            fontSize: "0.76rem", padding: "3px 10px", borderRadius: 5,
            background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.25)",
            color: "#4ade80", fontWeight: 600,
          }}>
            <i className="fa-solid fa-circle" style={{ fontSize: "0.5rem", marginRight: 5, verticalAlign: "middle" }}/>
            {t("reportes.anioActual")}: {fechaHasta}
          </span>
          <span style={{
            fontSize: "0.76rem", padding: "3px 10px", borderRadius: 5,
            background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.2)",
            color: "var(--text-secondary)", fontWeight: 600,
          }}>
            <i className="fa-solid fa-circle" style={{ fontSize: "0.5rem", marginRight: 5, verticalAlign: "middle" }}/>
            {t("reportes.anioAnterior")}: {antFecha}
            {loadingAnt && <i className="fa-solid fa-circle-notch fa-spin" style={{ marginLeft: 8, fontSize: "0.7rem" }}/>}
          </span>
        </div>
      )}

      <FiltrosBarra
        fechaHasta={fechaHasta} setFechaHasta={setFechaHasta}
        soloConMov={soloConMov} setSoloConMov={setSoloConMov}
        onActualizar={fetchData} loading={loading}
        comparativo={comparativo} setComparativo={setComparativo}
        extraButtons={
          <>
            <BotonExcel
              onClick={() => excelBG.descargar(`fecha_hasta=${fechaHasta}`)}
              loading={excelBG.loading}
              disabled={excelBG.loading || loading || !data}
            />
            <BotonPDF
              onClick={() => pdfBG.generar(`fecha_hasta=${fechaHasta}`)}
              loading={pdfBG.loading}
              disabled={pdfBG.loading || loading || !data}
            />
            {pdfBG.modal}
            {pdfBG.error && (
              <div style={{ padding: "6px 12px", background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6,
                color: "#f87171", fontSize: "0.8rem" }}>
                <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 5 }}/>
                {pdfBG.error}
              </div>
            )}
          </>
        }
      />

      {data && (
        <CardsResumen cards={[
          { label: t("reportes.totalActivos"),    value: Math.abs(tA),   prefix: tA   < 0 ? "-" : "", color: tA   < 0 ? "#f87171" : "#60a5fa", icon: "fa-building" },
          { label: t("reportes.totalPasivos"),    value: Math.abs(tP),   prefix: tP   < 0 ? "-" : "", color: "#f87171", icon: "fa-file-invoice" },
          { label: t("reportes.totalPatrimonio"), value: Math.abs(tPat), prefix: tPat < 0 ? "-" : "", color: tPat < 0 ? "#f87171" : "#c084fc", icon: "fa-landmark" },
          { label: bal ? t("reportes.balanceadoOk") : t("reportes.diferenciaWarn"),
            value: Math.abs(Number(data.diferencia)),
            color: bal ? "#4ade80" : "#fbbf24",
            icon:  bal ? "fa-scale-balanced" : "fa-triangle-exclamation" },
        ]}/>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableHeader
          count={total} loading={loading || loadingAnt}
          onColapsar={() => setCollapsed(new Set(filas.filter(f => f.nivel < 4).map(f => f.id)))}
          onExpandir={() => setCollapsed(new Set())}
        />
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 130 }}>{t("reportes.codigo")}</th>
                <th>{t("reportes.nombre")}</th>
                <th className="text-end" style={{ width: 160 }}>{t("reportes.saldo")}</th>
                {comparativo && (
                  <th className="text-end" style={{ width: 160, borderLeft: "1px solid var(--border-card)",
                    color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                    {t("reportes.anioAnterior")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading
                ? <FilaCargando colSpan={numCols} msg={t("reportes.calculandoBalanceGeneral")}/>
                : total === 0
                  ? <FilaVacia colSpan={numCols} soloConMov={soloConMov} onMostrarTodas={() => setSoloConMov(false)}/>
                  : <>
                    {activos.length > 0 && <>
                      <Separador label={t("reportes.activos")} color="#60a5fa" numCols={numCols}/>
                      {activos.map(f => (
                        <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                          onToggle={id => setCollapsed(prev => toggle(prev, id))}
                          colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}
                          extraCells={comparativo ? (
                            <td className="text-end" style={{
                              padding: "6px 12px", fontFamily: "monospace", fontSize: "0.82rem",
                              color: valorFilaAnt(f, dataAnt).color,
                              borderLeft: "1px solid var(--border-card)"
                            }}>
                              {valorFilaAnt(f, dataAnt).text}
                            </td>
                          ) : null}
                        />
                      ))}
                      <FilaTotal label={t("reportes.totalActivos")} monto={tA} montoAnt={tAAnt}
                        color="#60a5fa" comparativo={comparativo} fmtCRCSigned={fmtCRCSigned}/>
                    </>}

                    {pasivos.length > 0 && <>
                      <Separador label={t("reportes.pasivos")} color="#f87171" numCols={numCols}/>
                      {pasivos.map(f => (
                        <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                          onToggle={id => setCollapsed(prev => toggle(prev, id))}
                          colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}
                          extraCells={comparativo ? (
                            <td className="text-end" style={{
                              padding: "6px 12px", fontFamily: "monospace", fontSize: "0.82rem",
                              color: valorFilaAnt(f, dataAnt).color,
                              borderLeft: "1px solid var(--border-card)"
                            }}>
                              {valorFilaAnt(f, dataAnt).text}
                            </td>
                          ) : null}
                        />
                      ))}
                      <FilaTotal label={t("reportes.totalPasivos")} monto={tP} montoAnt={tPAnt}
                        color="#f87171" comparativo={comparativo} fmtCRCSigned={fmtCRCSigned}/>
                    </>}

                    {patrimonio.length > 0 && <>
                      <Separador label={t("reportes.patrimonio")} color="#c084fc" numCols={numCols}/>
                      {patrimonio.map(f => (
                        <FilaArbol key={f.id} fila={f} collapsed={collapsed}
                          onToggle={id => setCollapsed(prev => toggle(prev, id))}
                          colorFn={colorFila} valorFn={valorFila} bgFn={bgFila}
                          extraCells={comparativo ? (
                            <td className="text-end" style={{
                              padding: "6px 12px", fontFamily: "monospace", fontSize: "0.82rem",
                              color: valorFilaAnt(f, dataAnt).color,
                              borderLeft: "1px solid var(--border-card)"
                            }}>
                              {valorFilaAnt(f, dataAnt).text}
                            </td>
                          ) : null}
                        />
                      ))}
                      <FilaTotal label={t("reportes.totalPatrimonio")} monto={tPat} montoAnt={tPatAnt}
                        color="#c084fc" comparativo={comparativo} fmtCRCSigned={fmtCRCSigned}/>
                    </>}

                    {data && (
                      <>
                        <FilaTotal label={t("reportes.totalPasivoPatrimonio")} monto={tPP} montoAnt={tPPAnt}
                          color="var(--text-secondary)" big comparativo={comparativo} fmtCRCSigned={fmtCRCSigned}/>
                        <tr style={{ background: bal ? "rgba(249,115,22,0.08)" : "rgba(251,191,36,0.08)",
                          borderTop: `3px solid ${bal ? "#f97316" : "#fbbf24"}` }}>
                          <td colSpan={2} style={{ padding: "12px 12px", fontWeight: 800,
                            fontSize: "0.92rem", color: bal ? "#f97316" : "#fbbf24" }}>
                            {bal ? t("reportes.activosEquals") : t("reportes.diferenciaPasivos")}
                          </td>
                          <td className="text-end" style={{ padding: "12px 14px",
                            fontFamily: "monospace", fontWeight: 800, fontSize: "1rem",
                            color: bal ? "#4ade80" : "#fbbf24" }}>
                            {bal ? t("reportes.balanceadoCheck") : fmtCRCSigned(isUSD ? (tA - tPP) : Number(data.diferencia ?? 0))}
                          </td>
                          {comparativo && (
                            <td className="text-end" style={{ padding: "12px 14px",
                              fontFamily: "monospace", fontWeight: 800, fontSize: "1rem",
                              color: "var(--text-secondary)",
                              borderLeft: "1px solid var(--border-card)" }}>
                              {tPPAnt != null ? fmtCRCSigned(tAAnt) : "—"}
                            </td>
                          )}
                        </tr>
                      </>
                    )}
                  </>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
